// ============================================================================
// REQ-2026-0007 — Triaxial Analysis range-typed picker resolver
//
// Mirrors the Direct Shear `findPeakRow` pattern (see
// `analysis-plots/charts/MultiStageOverlayChart.tsx` and
// `analysis-plots/StageAnalysis.tsx`) but for Triaxial readings + the
// four slot semantics in the locked spec:
//
//   Max      → row with max deviator stress q within [from, to]
//   Peak     → row with max effective stress ratio σ'₁/σ'₃ within [from, to]
//   End      → row with max axial strain εₐ within [from, to] (= last point)
//   Residual → row with min q within [from, to] (post-peak strength)
//
// Range semantics:
//   - both null         → slot unset, no aggregation row.
//   - from set, to null → point-pick; snap to nearest εₐ row.
//   - both set          → aggregate over [min(from, to), max(from, to)].
//
// All slot defaults (Q7.2) are reversible per Gigi:
//   - Max / Peak: both currently default to "max deviator stress"; once Gigi
//     confirms naming, Peak's resolver may swap to "max stress ratio".
//   - End / Residual: defaults to "max strain" / "min q in range".
// ============================================================================

import type { TriaxialReading } from '../../../types/triaxial';

export type TriaxialSlot = 'max' | 'peak' | 'end' | 'residual';

export interface SlotRange {
  fromPct: number | null;
  toPct: number | null;
}

export interface TriaxialRanges {
  max: SlotRange;
  peak: SlotRange;
  end: SlotRange;
  residual: SlotRange;
}

/** Snapshot of a shear-reading row at the slot's aggregation point. */
export interface SlotSnapshot {
  axialStrainPct: number | null;
  deviatorStressKPa: number | null;
  cambridgePKPa: number | null;
  effCambridgePKPa: number | null;
  tauTKPa: number | null;
  meanStressSKPa: number | null;
  effStressRatio: number | null;
  excessPwpKPa: number | null;
}

export const EMPTY_SNAPSHOT: SlotSnapshot = {
  axialStrainPct: null,
  deviatorStressKPa: null,
  cambridgePKPa: null,
  effCambridgePKPa: null,
  tauTKPa: null,
  meanStressSKPa: null,
  effStressRatio: null,
  excessPwpKPa: null,
};

const num = (v: number | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function readingToSnapshot(r: TriaxialReading): SlotSnapshot {
  return {
    axialStrainPct: num(r.computedAxialStrainPct),
    deviatorStressKPa: num(r.computedDeviatorStressKPa),
    cambridgePKPa: num(r.computedCambridgePKPa),
    effCambridgePKPa: num(r.computedEffCambridgePKPa),
    tauTKPa: num(r.computedMaxShearStressTKPa),
    meanStressSKPa: num(r.computedMeanStressSKPa),
    effStressRatio: num(r.computedEffStressRatio),
    excessPwpKPa: num(r.computedExcessPwpKPa),
  };
}

/** Rows whose strain is non-null and lies in [lo, hi] (inclusive). */
function rowsInRange(
  shear: TriaxialReading[],
  lo: number,
  hi: number,
): TriaxialReading[] {
  return shear.filter((r) => {
    const eps = num(r.computedAxialStrainPct);
    return eps !== null && eps >= lo && eps <= hi;
  });
}

/** Snap to row with εₐ nearest the target (point-pick fallback). */
function nearestRowByStrain(
  shear: TriaxialReading[],
  target: number,
): TriaxialReading | null {
  let best: TriaxialReading | null = null;
  let bestDist = Infinity;
  for (const r of shear) {
    const eps = num(r.computedAxialStrainPct);
    if (eps === null) continue;
    const d = Math.abs(eps - target);
    if (d < bestDist) {
      bestDist = d;
      best = r;
    }
  }
  return best;
}

/**
 * Pick the aggregation row in `shear` for a slot, given its range and the
 * slot's aggregation rule.
 *
 * Returns null when:
 *   - both range bounds are null (slot unset)
 *   - the range collapses to a from-only point but no readings exist
 *   - both bounds are set but no row falls within them
 */
export function resolveSlotRow(
  slot: TriaxialSlot,
  range: SlotRange,
  shear: TriaxialReading[],
): TriaxialReading | null {
  const f = num(range.fromPct);
  const t = num(range.toPct);

  // Both null → slot unset
  if (f === null && t === null) return null;

  // Point-pick: from set, to null → snap to nearest εₐ row
  if (f !== null && t === null) {
    return nearestRowByStrain(shear, f);
  }
  if (f === null && t !== null) {
    return nearestRowByStrain(shear, t);
  }

  // Both bounds set → aggregate within [lo, hi]
  const lo = Math.min(f as number, t as number);
  const hi = Math.max(f as number, t as number);
  const candidates = rowsInRange(shear, lo, hi);
  if (candidates.length === 0) return null;
  if (candidates.length === 1) return candidates[0];

  switch (slot) {
    case 'max':
      // Max deviator stress q in range
      return candidates.reduce((best, r) => {
        const bq = num(best.computedDeviatorStressKPa) ?? -Infinity;
        const rq = num(r.computedDeviatorStressKPa) ?? -Infinity;
        return rq > bq ? r : best;
      });
    case 'peak':
      // Max effective stress ratio σ'₁/σ'₃ in range
      return candidates.reduce((best, r) => {
        const bs = num(best.computedEffStressRatio) ?? -Infinity;
        const rs = num(r.computedEffStressRatio) ?? -Infinity;
        return rs > bs ? r : best;
      });
    case 'end':
      // Max axial strain εₐ in range (= last point in window)
      return candidates.reduce((best, r) => {
        const be = num(best.computedAxialStrainPct) ?? -Infinity;
        const re = num(r.computedAxialStrainPct) ?? -Infinity;
        return re > be ? r : best;
      });
    case 'residual':
      // Min deviator stress q in range (post-peak residual strength)
      return candidates.reduce((best, r) => {
        const bq = num(best.computedDeviatorStressKPa) ?? Infinity;
        const rq = num(r.computedDeviatorStressKPa) ?? Infinity;
        return rq < bq ? r : best;
      });
    default:
      return null;
  }
}

/**
 * Convenience wrapper that returns the snapshot block stored on the
 * persisted TriaxialGeneral row.
 */
export function resolveSlotSnapshot(
  slot: TriaxialSlot,
  range: SlotRange,
  shear: TriaxialReading[],
): SlotSnapshot {
  const row = resolveSlotRow(slot, range, shear);
  if (!row) return { ...EMPTY_SNAPSHOT };
  return readingToSnapshot(row);
}
